import { EXXONMOBIL_ID, SHELL_ID } from './ids';

type AppConfig = {
	enableFVIReportEmbedding: boolean;
	requireImagesToPublish: boolean;
	enableContactPerson: boolean;
	enableEarlyClientAccess: boolean;
	enableNewImageOrdering: boolean;
	preferS3ToCloudinary: boolean;
	preventImageCaching: boolean;
	enableSentry: boolean;
	enableServiceWorkers: boolean;
	enableViewLogs: boolean;
	enableReportCompletionBeforePublish: boolean;
	enableCacheDeletion: boolean;
	useBase64OfflineImage: boolean;
	uploadExcelDocument: boolean;
	archiveProjects: boolean;
	enablePDFToPngConversion: boolean;
	enableExcelToPngConversion: boolean;
	enableMicrosoftLogin: boolean;
	enableRiskRating: boolean;
	enableReportComment: boolean;
	saveLivePreview: boolean;
	showWalkThrough: boolean;
	supportedTemplates: {
		helideck: string;
		helideckNetDrop: string;
		helideckTieDown: string;
		et: string;
		pec: string;
		paut: string;
		dpt: string;
		rtw: string;
		mt: string;
		mut: string;
		vtPiping: string;
		vtEquipment: string;
		vtStructure: string;
		visualInspection: string;
		hardnessInspectionReport: string;

		exxonMobilAUT: string;
		exxonMobilPEC: string;
		exxonMobilFVIPiping: string;
		exxonMobilFVIEquipment: string;
		exxonMobilFVIStructure: string;
		exxonMobilUTMPiping: string;
		exxonMobilUTMEquipment: string;
		exxonMobilUTMStructure: string;
		exxonMobilFVITank: string;
		exxonMobilLPT: string;
		exxonMobilMPI: string;
		exxonMobilET: string;
		exxonMobilDRT: string;
		exxonMobilRiser: string;
		exxonMobilSWUT: string;
		tankUTM: string;
		pautNew: string;
		pcnlStructuralIntegrityReport: string;
		tofd: string;
		ndtStructuralReport: string;
		pmi: string;
		pdo: string;
		pdrt: string;
		lrut: string;
		swut: string;
		gre: string;
		cdr: string;
		infrared: string;
	};
	coverDataTemplates: {
		pdrt: string;
		exxonMobilFVIPiping: Record<string, string>;
		exxonMobilFVITank: Record<string, string>;
		exxonMobilFVIEquipment: Record<string, string>;
		exxonMobilFVIStructure: Record<string, string>;
		exxonMobilUTMPiping: Record<string, string>;
		exxonMobilUTMEquipment: Record<string, string>;
		exxonMobilUTMStructure: Record<string, string>;
		pautNew: Record<string, string>;
		tofd: Record<string, string>;
		exxonMobilPEC: Record<string, string>;
		exxonMobilAUT: Record<string, string>;
		infrared: Record<string, string>;
		tankUTM: string;
		lrut: string;
		swut: string;
		exxonMobilDRT: string;
		pcnlStructuralIntegrityReport: string;
		exxonMobilLPT: string;
		exxonMobilET: string;
		exxonMobilMPT: string;
		exxonMobilRiser: string;
		exxonMobilSWUT: string;
		ndtStructuralReport: string;
		cdr: string;
		pmi: string;
		hardnessInspectionReport: string;
	};
	trackableTemplates: string[];
};

// TO DO -> move the cover data templates logic to the backend
const appConfig: AppConfig = {
	enableFVIReportEmbedding: true,
	requireImagesToPublish: true,
	enableContactPerson: false,
	enableEarlyClientAccess: true,
	enableNewImageOrdering: true,
	preventImageCaching: false,
	enableSentry: process.env.NODE_ENV === 'production',
	enableServiceWorkers: true,
	preferS3ToCloudinary: true,
	enableViewLogs: true,
	enableMicrosoftLogin: process.env.NODE_ENV === 'development',
	enableReportCompletionBeforePublish: process.env.NODE_ENV === 'production',
	enableReportComment: true,
	useBase64OfflineImage: true,
	enableCacheDeletion: true,
	uploadExcelDocument: true,
	archiveProjects: true,
	enablePDFToPngConversion: true,
	enableExcelToPngConversion: false,
	enableRiskRating: false,
	showWalkThrough: true,
	saveLivePreview: false,
	supportedTemplates: {
		helideck: '63506f7d3bd54a8592fe24a0',
		helideckNetDrop: '63506f7d3bd54a8592fe24a1',
		helideckTieDown: '63506f7d3bd54a8592fe24a2',
		et: '63506f7d3bd54a8592fe249f',
		pec: '63506f7d3bd54a8592fe24a6',
		paut: '63506f7d3bd54a8592fe24a5',
		dpt: '63506f7d3bd54a8592fe24a7',
		rtw: '63506f7d3bd54a8592fe24a9',
		mt: '63506f7d3bd54a8592fe24a3',
		mut: '63506f7d3bd54a8592fe24a4',
		vtPiping: '63506f7d3bd54a8592fe24aa',
		vtEquipment: '63506f7d3bd54a8592fe24ac',
		vtStructure: '63506f7d3bd54a8592fe24ab',
		visualInspection: '642bd1f1d5009c930d74462c',

		exxonMobilPEC: '65bf5e405a0ae61918a1b65d',
		exxonMobilAUT: '65bee1a05a0ae61918a19e47',
		exxonMobilFVIPiping: '64ff017c2fe818c83314ac57',
		exxonMobilFVIEquipment: '64ff02602fe818c83314ac83',
		exxonMobilFVIStructure: '65ae51019a6815c7fb5fba23',
		exxonMobilFVITank: '66ab57d154c461e0d5d092b8',
		exxonMobilDRT: '64ff0aeb2fe818c83314ae5c',
		exxonMobilLPT: '64ff0bca2fe818c83314af23',
		exxonMobilMPI: '64ff11142fe818c83314b00e',
		exxonMobilET: '64ff11c22fe818c83314b012',
		exxonMobilSWUT: '66fd5e997c875252815d095c',
		exxonMobilRiser: '66bf7580dd6e7cc9841fc9cb',
		exxonMobilUTMPiping: '660d63c13b81dd4369eb8541',
		exxonMobilUTMEquipment: '661bdb0e9e9d3926b42659af',
		exxonMobilUTMStructure: '661c40b69e9d3926b4266dc5',
		pautNew: '6748b2aedf12852fd9bcf10d',
		pcnlStructuralIntegrityReport: '66584cef5e35d7407c655602',
		tofd: '67540620af1cc372dbc086af',
		ndtStructuralReport: '678fa04e4e8430e9f7a9e3da',
		tankUTM: '67ad8cdde4ad715246793200',
		lrut: '67c4befd61f613f609028ba4',
		pdo: '67c9d0c3260dff6ec7c12dc2',
		pmi: '67c842e950d072a3e73666b8',
		pdrt: '63506f7d3bd54a8592fe24a8',
		swut: '66fd5e997c875252815d095c',
		gre: '67ed06a50c142f488ba18163',
		cdr: '680f49f2e35e30ef590a3e94',
		infrared: '688b5ef0075525cfa5a8642a',
		hardnessInspectionReport: '69811c092d0d59af973c32bc',
	},
	coverDataTemplates: {
		hardnessInspectionReport: '69821855ad85171180d3e094',
		swut: '67d6ba3ed69684f01e06c9bd',
		pdrt: '676a17abb7e723617f744529',
		lrut: '67c5fa6f2a9bebab29708fe4',
		tankUTM: '67ad8feee4ad715246796c28',
		exxonMobilDRT: '676e7bf960f8cb4027aada50',
		pcnlStructuralIntegrityReport: '665d88b5c2d8fd94a8c60b5c',
		exxonMobilLPT: '6662b4b3394c694de27f544c',
		exxonMobilET: '6668dd7098cc381f22aed491',
		exxonMobilMPT: '668fb27eed47296a91c91bc8',
		exxonMobilSWUT: '',
		exxonMobilRiser: '66bf8b3cdd6e7cc9842014c9',
		ndtStructuralReport: '678fb2834e8430e9f7aa1020',
		cdr: '',
		pmi: '687ed4262cd7ba2f399c17c3',

		infrared: {
			[EXXONMOBIL_ID]: '6898f258d1c82d1ce55c0240',
			[SHELL_ID]: '6746d9c66662874288a6b185',
		},
		exxonMobilAUT: {
			[EXXONMOBIL_ID]: '66ab3a7f54c461e0d5d07880',
			[SHELL_ID]: '6746d9c66662874288a6b185',
		},
		exxonMobilFVIStructure: {
			[EXXONMOBIL_ID]: '65fda8d498b48474dfcb1a5d',
			[SHELL_ID]: '6746d9c66662874288a6b185',
		},
		exxonMobilFVIEquipment: {
			[EXXONMOBIL_ID]: '65fda2f598b48474dfcb13a8',
			[SHELL_ID]: '6746d9c66662874288a6b185',
		},
		exxonMobilFVITank: {
			[EXXONMOBIL_ID]: '66def047333ef941266c6487',
			[SHELL_ID]: '6746d9c66662874288a6b185',
		},
		exxonMobilFVIPiping: {
			[EXXONMOBIL_ID]: '679a608f086e66c9a4ea6751',
			[SHELL_ID]: '6746d9c66662874288a6b185',
		},
		exxonMobilPEC: {
			[EXXONMOBIL_ID]: '663db36e506dfefa81e40acd',
			[SHELL_ID]: '6746d9c66662874288a6b185',
		},
		exxonMobilUTMPiping: {
			[EXXONMOBIL_ID]: '6617ea6725c84ccd19d86a7f',
			[SHELL_ID]: '6746d9c66662874288a6b185',
		},
		exxonMobilUTMEquipment: {
			[EXXONMOBIL_ID]: '661bdd969e9d3926b4266431',
			[SHELL_ID]: '6746db796662874288a6b8df',
		},
		exxonMobilUTMStructure: {
			[EXXONMOBIL_ID]: '661c502d9e9d3926b4267a26',
			[SHELL_ID]: '6746dc1b6662874288a6bff6',
		},
		pautNew: {
			[EXXONMOBIL_ID]: '65160954e682cb81fdf2e7c1',
			[SHELL_ID]: '6748fb08df12852fd9bd2c41'
		},
		tofd: {
			[EXXONMOBIL_ID]: '',
			[SHELL_ID]: '6759a6d78cf37395b377afcc'
		},
	},
	get trackableTemplates() {
		return [
			this.supportedTemplates.exxonMobilFVIEquipment,
			this.supportedTemplates.exxonMobilFVIPiping,
			this.supportedTemplates.exxonMobilFVIStructure,
			this.supportedTemplates.exxonMobilFVITank,
			this.supportedTemplates.exxonMobilAUT,
			this.supportedTemplates.pcnlStructuralIntegrityReport,
			this.supportedTemplates.exxonMobilUTMEquipment,
			this.supportedTemplates.exxonMobilUTMPiping,
			this.supportedTemplates.exxonMobilUTMStructure
		];
	},
};

export default appConfig;
